import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import {
  QueryClient,
  QueryClientProvider,
  MutationCache,
} from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { router } from "./router";
import { initPageViewTracking, initPresenceHeartbeat, setAnalyticsVenue } from "./lib/analytics";
import { installClientErrorCapture, reportClientError, trackAdminAction } from "./lib/observability";
import { initEnvTitle } from "./lib/env-title";
import { ConsentBanner } from "./components/consent-banner";
import { Suspense } from "react";
import { useVenue } from "./lib/venue";
import { shouldRenderPlatformLegalPage } from "./lib/router-decisions";
// Platform (apex / non-venue-host) pages: a venue visitor never renders these,
// so lazy-load them out of the entry chunk. A Suspense boundary around <Root/>
// (below) covers their async load with the neutral splash.
const MarketingLanding = React.lazy(() =>
  import("./pages/marketing").then((m) => ({ default: m.MarketingLanding })),
);
const OnboardingPage = React.lazy(() =>
  import("./pages/onboarding").then((m) => ({ default: m.OnboardingPage })),
);
const AdminDashboardPage = React.lazy(() =>
  import("./pages/admin-dashboard").then((m) => ({ default: m.AdminDashboardPage })),
);
const TalkToUsPage = React.lazy(() =>
  import("./pages/talk-to-us").then((m) => ({ default: m.TalkToUsPage })),
);
const SolutionPage = React.lazy(() =>
  import("./pages/solution-page").then((m) => ({ default: m.SolutionPage })),
);
const LegalPage = React.lazy(() =>
  import("./pages/legal").then((m) => ({ default: m.LegalPage })),
);
import { Toaster, pushToast } from "./components/toast";
import {
  registerDeepLinkHandler,
  installSmartAppBanner,
  registerSessionRefresh,
} from "./lib/native-links";
import { revalidateSession, useAuth, hasCachedUser } from "./lib/auth";
import { isNativeApp } from "./lib/platform";
import { setAuthExpiredHandler } from "./lib/api";
import "./globals.css"; // Tailwind + ported design tokens (must precede styles.css)
import "./styles.css";

// Stale-deploy resilience. When a lazily-imported route chunk fails to load,
// its hashed filename no longer exists because a newer build replaced it, which
// happens when a tab was open across a deploy, Vite fires `vite:preloadError`.
// Without this it white-screens ("Failed to fetch dynamically imported module").
// Reload ONCE to pick up the fresh build; a short-lived flag stops a genuinely
// broken load from loop-reloading. Critical for the always-open POS/host tablet.
window.addEventListener("vite:preloadError", (event) => {
  const KEY = "assembly:chunk-reload-at";
  const last = Number(sessionStorage.getItem(KEY) || "0");
  if (Date.now() - last < 10_000) return; // just tried, let the error surface
  sessionStorage.setItem(KEY, String(Date.now()));
  event.preventDefault();
  window.location.reload();
});

// Universal links (NFC tags etc.) must route the native shell as soon as the
// app opens, regardless of which screen renders, register once at startup.
registerDeepLinkHandler();

// Keep the displayed auth state honest. Re-check the session (1) when the app
// returns to the foreground and (2) whenever an authed request 401s. The
// server is the source of truth, so a transient 401 won't sign anyone out,
// but a genuine expiry flips useSession() to logged-out and the guards react.
registerSessionRefresh(revalidateSession);
setAuthExpiredHandler(revalidateSession);

// GTM/GA4 SPA page-view tracking. No-op on non-Madruga hosts (GTM isn't loaded
// there). Registered before render so the initial route resolution is captured.
initPageViewTracking(router);
installClientErrorCapture();

// First-party "people on site right now" heartbeat, see
// web/src/lib/analytics.ts's initPresenceHeartbeat.
initPresenceHeartbeat();

// Tag the browser tab with the environment ([STAGING]/[DEV]) so staging + prod
// tabs are distinguishable; prod stays clean.
initEnvTitle();

// Native cold-launch auth gate. On a fresh app open, better-auth's useSession()
// starts pending → the venue pages would render their signed-OUT UI (e.g. "Sign
// in to subscribe") for the split second before get-session resolves, which
// reads as "not signed in". Hold the neutral splash until the FIRST check
// settles so a signed-in user never sees that flash. Only in the native app,
// on the web the brief flash is acceptable and instant first paint / SEO matter
// more. Latches after the first resolution (later foreground revalidations must
// not re-show the splash), and a 3s timeout fails open so an offline launch can
// never hang on the splash.
function VenueApp() {
  const { isLoading } = useAuth();
  // Skip the splash entirely when we have a cached user (#2): the app renders
  // signed-in optimistically, so there's nothing to hide. Splash only on native
  // when there's no last-known user (first-ever launch / after sign-out).
  const [settled, setSettled] = useState(() => !isNativeApp() || hasCachedUser());
  useEffect(() => {
    if (!isLoading) setSettled(true);
  }, [isLoading]);
  useEffect(() => {
    const t = setTimeout(() => setSettled(true), 3000);
    return () => clearTimeout(t);
  }, []);
  if (!settled) {
    // Use the venue's own background (applyVenueTheme sets --bg from the cached
    // venue on load) so a custom/light-themed venue doesn't flash near-black
    // before its theme paints; #0a0a0b is only the pre-theme fallback. Matches
    // the draft-gate splash pattern.
    return <div style={{ minHeight: "100dvh", background: "var(--bg, #0a0a0b)" }} aria-hidden />;
  }
  return <RouterProvider router={router} />;
}

// Root switch: a host that resolves to a venue gets the venue app; the apex /
// any non-venue host (e.g. assemblytickets.com) gets the marketing landing.
function Root() {
  const { data } = useVenue();
  // Safari-only App Store banner for venues with a published iOS app,
  // the "download the app" path for NFC taps outside the app.
  useEffect(() => {
    installSmartAppBanner(data?.venue?.appConfig?.appStoreId);
  }, [data?.venue?.appConfig?.appStoreId]);
  // Stamp analytics events with this venue + lay on any self-service GTM/GA4
  // overrides it's configured (additive to Assembly's own base tracking).
  // Deps are the primitive analytics fields, not the whole appConfig object,
  // react-query hands back a new appConfig reference on every refetch even
  // when the content is unchanged, which would otherwise re-fire this on
  // every background refetch (setAnalyticsVenue dedupes internally too, but
  // there's no reason to call it more than necessary).
  const analyticsCfg = data?.venue?.appConfig?.analytics;
  useEffect(() => {
    setAnalyticsVenue(
      data?.venue
        ? { slug: data.venue.slug, archetype: data.venue.archetype, appConfig: data.venue.appConfig }
        : null,
    );
  }, [data?.venue?.slug, data?.venue?.archetype, analyticsCfg?.gtmContainerId, analyticsCfg?.ga4MeasurementId]);
  // /get-started is a platform-level route, the self-serve onboarding, so it
  // works on ANY host (the apex, an unclaimed subdomain, or even an existing
  // venue's host). Check it BEFORE venue resolution: on a venue host the
  // router has no such route and would 404 otherwise.
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  if (path === "/get-started" || path.startsWith("/get-started/")) {
    return <OnboardingPage />;
  }
  if (path === "/talk-to-us") {
    return <TalkToUsPage />;
  }
  // Legal pages fall back to the platform default (Assembly LegalPage) UNLESS
  // a venue has authored its own published site page with slug "terms" /
  // "privacy" in the website builder. Then that venue's page wins on their
  // own subdomain and the flow falls through to the venue app which renders
  // it via <SitePagesSite>. Rule is unit-tested in router-decisions.test.ts.
  const venueSitePages = data?.venue?.appConfig?.sitePages;
  if (path === "/privacy" && shouldRenderPlatformLegalPage({ slug: "privacy", sitePages: venueSitePages })) {
    return <LegalPage initialTab="privacy" />;
  }
  if (path === "/terms" && shouldRenderPlatformLegalPage({ slug: "terms", sitePages: venueSitePages })) {
    return <LegalPage initialTab="terms" />;
  }
  if (path.startsWith("/solutions/") || path.startsWith("/products/")) {
    return <SolutionPage />;
  }
  // Clean 404 (host has no venue, e.g. the apex) → marketing site OR, under
  // /admin, the Assembly platform-admin dashboard (auth enforced server-side).
  // Note: /admin is gated to the NON-venue host on purpose, on a venue host
  // /admin is that venue's own admin (handled by the router below).
  if (data === null) {
    if (path === "/admin" || path.startsWith("/admin/")) {
      return <AdminDashboardPage />;
    }
    return <MarketingLanding />;
  }
  // Venue resolved (incl. from cache → instant, no flash) → the venue app,
  // behind the native cold-launch auth gate (see VenueApp).
  if (data?.venue) return <VenueApp />;
  // First-ever load / transient error: a neutral splash, NOT the marketing
  // landing, so a venue never flashes the indigo marketing screen before its
  // own theme paints.
  return <div style={{ minHeight: "100dvh", background: "#0a0a0b" }} aria-hidden />;
}

const queryClient = new QueryClient({
  // Safety net: any mutation that fails without its own handling surfaces a
  // global error toast, so no form submit / admin action can fail silently.
  // Mutations that opt out (because they render their own inline error and
  // don't want a duplicate toast) set meta.silentError = true.
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      // Capture EVERY mutation failure for the Observability tab (even ones
      // that suppress their toast), a broken admin action we'd otherwise
      // never hear about.
      reportClientError(
        error instanceof Error && error.message ? error.message : "mutation failed",
        error instanceof Error ? error.stack : undefined,
      );
      if (mutation.meta?.silentError) return;
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Something went wrong. Please try again.";
      pushToast(message, "error");
    },
    onSuccess: (_data, _vars, _ctx, mutation) => {
      // Admin-action telemetry: any mutation tagged meta.adminAction is logged
      // as an adoption signal (campaign.saved, branding.saved…).
      const action = mutation.meta?.adminAction;
      if (typeof action === "string") trackAdminAction(action);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

const root = document.getElementById("root");
if (!root) throw new Error("#root not found");

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <Suspense
        fallback={<div style={{ minHeight: "100dvh", background: "var(--bg, #0a0a0b)" }} aria-hidden />}
      >
        <Root />
      </Suspense>
      <Toaster />
      <ConsentBanner />
    </QueryClientProvider>
  </React.StrictMode>,
);
